import { observer } from 'mobx-react-lite'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { AppPaper } from 'src/component/common/analog/molecule/AppPaper'
import { useSkillAssessmentStore } from '../store/skill-assessment.store'
import { addSkillAssessment } from '../api/skill-assessment-api'
import { SkillSetLevel } from '../model/skill-assessment-model'
import { ModalWindow } from 'src/component/common/analog/template/ModalWindow'
import { useEffect, useState } from 'react'
import { Typography, useTheme, Zoom } from '@mui/material'
import { getCardRatingColor } from 'src/feature/my-team/util/skillGapUtils'
import { ColorTokens } from 'src/feature/theme/model/Palette'

const RATING_BOX_VALUES = [1, 2, 3, 4, 5] as const

export type SkillAssessmentLevelsProps = {
  isModalClose: () => void
  isModalOpen: boolean
  skillAssessmentSetIndex: number
  categoryId?: string
}

export const SkillAssessmentLevels = observer((props: SkillAssessmentLevelsProps) => {
  const skillAssessmentStore = useSkillAssessmentStore()
  const theme = useTheme()

  const skillAssessmentSet = props.categoryId
    ? skillAssessmentStore.skillSets?.find(cat => cat.id === props.categoryId)
    : skillAssessmentStore.skillSets?.[0]
  const skills = skillAssessmentSet?.skills ?? []
  const [expandedSkillIndex, setExpandedSkillIndex] = useState<number>(props.skillAssessmentSetIndex)

  const addUserSkillAssessment = async (skillId: string, selectedSetLevel: number) => {
    await addSkillAssessment(skillId, selectedSetLevel)

    const updatedSkill = skills.find(skill => skill.id === skillId)

    if (updatedSkill) {
      updatedSkill.selectedSetLevel = selectedSetLevel
    }
  }

  useEffect(() => {
    setExpandedSkillIndex(props.skillAssessmentSetIndex)
  }, [props.skillAssessmentSetIndex])

  const handleExpandSkill = (skillIndex: number) => {
    setExpandedSkillIndex(skillIndex)
  }

  const getRatingBoxSx = (rating: number, selectedRating: number) => {
    if (!selectedRating) {
      return {
        ...styles.ratingBox,
        borderColor: ColorTokens.border100,
        backgroundColor: 'background.dark',
        color: 'text.primary',
      }
    }

    const boxColor = getCardRatingColor(selectedRating)
    const isFilled = selectedRating >= rating

    return {
      ...styles.ratingBox,
      borderColor: boxColor,
      backgroundColor: isFilled ? boxColor : 'common.white',
      color: isFilled ? 'common.white' : boxColor,
    }
  }

  return (
    <ModalWindow
      modalProps={{
        open: props.isModalOpen,
        onClose: () => props.isModalClose(),
        PaperProps: {
          sx: {
            maxWidth: { xs: '90%', md: '80%', lg: '50%' },
            borderRadius: '12px',
          },
        },
      }}
      titleProps={{
        textValue: skillAssessmentSet?.category_name ?? '',
      }}
      subtitleProps={{
        textValue: skillAssessmentSet?.category_description ?? '',
        sx: { color: 'text.secondary' },
      }}
    >
      <Zoom in={props.isModalOpen} timeout={theme.transitions.duration.standard} mountOnEnter unmountOnExit>
        <AppBox sx={styles.mainContainer}>
          <AppBox sx={styles.skillList}>
            {skills.map((skill, skillIndex) => {
              const isExpanded = expandedSkillIndex === skillIndex
              const skillLevels = [...(skill.levels ?? [])].sort((a, b) => a.setLevel - b.setLevel)

              return (
                <AppPaper
                  key={skill.id}
                  onClick={() => handleExpandSkill(skillIndex)}
                  sx={{
                    ...styles.skillCard,
                    backgroundColor: isExpanded ? ColorTokens.primary10 : 'background.paper',
                  }}
                >
                  <AppBox sx={styles.skillSummaryRow}>
                    <AppBox sx={styles.skillTextColumn}>
                      <Typography variant='h5'>{skill.setName}</Typography>
                      <Typography variant='body2' sx={{ color: 'text.secondary' }}>{skill.setHint}</Typography>
                    </AppBox>

                    <AppBox sx={styles.ratingBoxes}>
                      {(() => {
                        const selectedRating = skill.selectedSetLevel ?? 0

                        return RATING_BOX_VALUES.map(rating => (
                          <AppBox
                            key={`${skill.id}-${rating}`}
                            component='button'
                            type='button'
                            sx={getRatingBoxSx(rating, selectedRating)}
                            onClick={() => addUserSkillAssessment(skill.id, rating)}
                            aria-label={`${skill.setName} level ${rating}`}
                          >
                            {rating}
                          </AppBox>
                        ))
                      })()}
                    </AppBox>
                  </AppBox>

                  {isExpanded && (
                    <AppBox sx={styles.expandedLevels}>
                      {skillLevels.map((level: SkillSetLevel, index: number) => (
                        <Typography
                          key={level.id}
                          onClick={() => addUserSkillAssessment(skill.id, level.setLevel)}
                          sx={{
                            fontSize: 12,
                            cursor: 'pointer',
                            color: level.setLevel !== skill.selectedSetLevel ? 'text.primary' : getCardRatingColor(level.setLevel),
                            '&:hover': {
                              textDecoration: 'underline',
                            },
                          }}
                        >
                          {`${index + 1}. ${level.setLevelDescription}`}
                        </Typography>
                      ))}
                    </AppBox>
                  )}
                </AppPaper>
              )
            })}
          </AppBox>
        </AppBox>
      </Zoom>
    </ModalWindow>
  )
})

const styles = {
  mainContainer: {
    marginTop: '0.5rem',
    flexDirection: 'column',
    gap: 10,
    width: { xs: '100%' },
  },
  skillList: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  skillCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    padding: '10px 15px',
    borderRadius: '12px',
    border: '1px solid',
    borderColor: 'transparent',
    cursor: 'pointer',
    transition: 'border-color 0.2s ease-in-out',
    '&:hover': {
      borderColor: ColorTokens.border100,
    },
  },
  skillSummaryRow: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
  },
  skillTextColumn: {
    flexDirection: 'column',
    minWidth: 0,
  },
  ratingBoxes: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    flexShrink: 0,
    flexWrap: 'nowrap',
  },
  ratingBox: {
    width: 30,
    height: 30,
    borderRadius: '6px',
    border: '1px solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 500,
    padding: 0,
    cursor: 'pointer',
    transition: 'transform 0.2s ease, background-color 0.2s ease, color 0.2s ease',
    '&:hover': {
      transform: 'translateY(-1px)',
    },
  },
  expandedLevels: {
    flexDirection: 'column',
    gap: 5,
    width: '100%',
  },
}
